/**
 * ProcessedImageContext
 * 
 * Provides a proper React context for sharing processed image data between components.
 * Replaces the problematic window.guestConverterProcessedImage and window.tutorialProcessedImage
 * global variables which could cause memory leaks and weren't reactive.
 * 
 * Usage:
 * - GuestConverter: Sets guestProcessedImage when file is loaded
 * - Home: Sets tutorialProcessedImage when tutorial sample is loaded
 * - Converter: Consumes either and clears after use
 */

import { createContext, useContext, useState, useCallback } from "react";

const ProcessedImageContext = createContext(null);

export function useProcessedImage() {
  const context = useContext(ProcessedImageContext);
  if (!context) {
    throw new Error("useProcessedImage must be used within ProcessedImageProvider");
  }
  return context;
}

export function ProcessedImageProvider({ children }) {
  // Processed image from guest converter flow
  const [guestProcessedImage, setGuestProcessedImage] = useState(null);
  
  // Processed image from tutorial flow
  const [tutorialProcessedImage, setTutorialProcessedImage] = useState(null);

  // Clear guest processed image after use
  const clearGuestProcessedImage = useCallback(() => {
    setGuestProcessedImage(null);
  }, []);

  // Clear tutorial processed image after use
  const clearTutorialProcessedImage = useCallback(() => {
    setTutorialProcessedImage(null);
  }, []);

  // Clear all processed images
  const clearAllProcessedImages = useCallback(() => {
    setGuestProcessedImage(null);
    setTutorialProcessedImage(null);
  }, []);

  // Get the active processed image (guest takes priority)
  const getActiveProcessedImage = useCallback(() => {
    return guestProcessedImage || tutorialProcessedImage;
  }, [guestProcessedImage, tutorialProcessedImage]);

  // Check if there's any pending processed image
  const hasPendingProcessedImage = useCallback(() => {
    return !!(guestProcessedImage || tutorialProcessedImage);
  }, [guestProcessedImage, tutorialProcessedImage]);

  const value = {
    // Guest converter flow
    guestProcessedImage,
    setGuestProcessedImage,
    clearGuestProcessedImage,
    
    // Tutorial flow
    tutorialProcessedImage,
    setTutorialProcessedImage,
    clearTutorialProcessedImage,
    
    // Utilities
    clearAllProcessedImages,
    getActiveProcessedImage,
    hasPendingProcessedImage,
  };

  return (
    <ProcessedImageContext.Provider value={value}>
      {children}
    </ProcessedImageContext.Provider>
  );
}

export default ProcessedImageContext;
